<template>
    <header class="header"
        v-bind:class="{
            'header--mobile-search-visible': searchVisible,
            'header-main': showHeaderMain,
            'header-no-shadow': isMobile && isSettingsOpened,
            'header--hidden': headerIsHidden,
            'header--scrolled': isScrolled && !showHeaderMain,
        }">
        <div class="header__container container">
            <div class="header__brand">
                <ui-link class="header__logo-link" v-bind:to="{ name: 'index' }" v-on:click.native="closeSettings">
                    <svg v-pre class="header__logo" fill="none" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                        <path stroke="currentColor" stroke-width="2.5" d="M46.54 19.143c.608.424.92 2.325.605 6.494a42.027 42.027 0 0 1-.606 4.514c1.411 2.459 1.8 5.247 1.167 8.366-.561 2.761-2.398 5.341-5.345 7.805C39.413 48.787 35.995 51 31.969 51c-4.027 0-8.154-2.323-10.66-4.678-2.507-2.354-4.084-4.575-4.813-7.047-.883-2.997-.587-6.038.886-9.124-.313-1.654-.51-3.158-.59-4.514-.296-4.994-.106-5.965.59-6.494.947-.72 3.961 1.13 9.042 5.547 2.327-.421 4.186-.631 5.576-.631 1.39 0 3.228.21 5.513.63 5.114-4.326 8.123-6.175 9.026-5.546Z"/>
                        <path fill="currentColor" d="M31.932 43.822c-1.234.024-2.896-1.649-2.896-2.333 0-.685 1.948-.888 3.037-.888 1.09 0 2.897.116 2.897.888 0 .771-1.804 2.309-3.038 2.333ZM23.985 37.338c1.785.695 3.59.315 4.03-.85.44-1.165-.65-2.674-2.435-3.37-1.784-.695-3.305-.775-3.746.39-.44 1.165.367 3.134 2.151 3.83ZM39.96 37.137c-1.687.815-3.525.516-3.965-.65-.44-1.164.65-2.673 2.434-3.369 1.785-.695 3.127-.775 3.567.39.44 1.165-.349 2.813-2.036 3.629Z"/>
                    </svg>
                </ui-link>

                <div class="header__links" v-if="!isMobile" ref="linksNav">
                    <!-- One underline glides along the bottom edge of the bar between destinations: -->
                    <span class="header-nav-thumb" v-bind:style="navThumbStyle"/>

                    <div class="header-link"
                        v-for="link in links"
                        v-bind:key="`header_link_${link}`"
                        v-bind:class="{ 'header-link--active': $route.name && ($route.name.startsWith(link) || (link === 'apps' && $route.name === 'app') || (link === 'blocks' && $route.name.startsWith('block'))) }">
                        <ui-link class="header-link__anchor" v-bind:to="{ name: link }">
                            <span class="header-link__label">{{$t(`header.links.${link}`)}}</span>
                        </ui-link>
                    </div>

                    <div v-if="!IS_TESTNET"
                        class="header-link"
                        v-bind:class="{ 'header-link--active': $route.name && ($route.name === 'market' || $route.name === 'whales' || $route.name === 'validators' || $route.name === 'config' || $route.name === 'tools') }"
                        v-on:mouseleave="dropdownLeave">
                        <span class="header-link__anchor" v-on:mouseenter="dropdownEnter">
                            <span class="header-link__label">{{$t(`header.links.more`)}}</span>
                        </span>
                        <transition name="fade">
                            <app-header-dropdown v-if="canShowDropdown" />
                        </transition>
                    </div>
                </div>
            </div>

            <div class="header__search" v-show="$route.name !== 'index'">
                <app-search show-loopa class="header-search"
                    v-bind:placeholder="$t('header.search_placeholder')"
                    v-bind:focusInputField="searchVisible"
                    v-on:focus="showHeader()"
                    v-on:collapseMobileSearch="searchVisible = false"/>
            </div>

            <nav class="header__actions">
                <!-- We must stop click propagation, so that app-search global click handler
                     doesn't detect click on that icon. Otherwise the search field will collapse
                     instantly, because click on the icon is considered as outside click. -->
                <button type="button" class="header-icon-button header-icon-button--loopa"
                    v-if="$route.name !== 'index'"
                    v-on:click.stop="/* <-- .stop must be here */ searchVisible = true">
                    <icon-loopa/>
                </button>

                <div v-if="!isMobile && canShowAddressbook" class="header-addressbook-container">
                    <button type="button" class="header-icon-button" v-on:click="showAddressbook = !showAddressbook">
                        <icon-addressbook/>
                    </button>
                    <transition name="fade">
                        <app-header-addressbook v-if="showAddressbook" />
                    </transition>
                </div>

                <div class="header-addressbook-container">
                    <button type="button" class="header-icon-button header-icon-button--menu"
                        v-bind:class="{ 'header-icon-button--pressed': !isMobile && isSettingsOpened }"
                        v-on:click="toggleSettings">
                        <icon-burger v-if="isMobile && !isSettingsOpened"/>
                        <icon-close v-else-if="isMobile && isSettingsOpened"/>
                        <icon-settings v-else/>
                    </button>
                    <transition name="fade">
                        <div class="settings-dropdown" v-if="!isMobile && isSettingsOpened" v-on:keydown.esc="closeSettings">
                            <app-settings compact/>
                        </div>
                    </transition>
                </div>
            </nav>
        </div>

        <div class="addressbook-overlay"
            v-if="showAddressbook || (!isMobile && isSettingsOpened)"
            v-on:click="showAddressbook = false; closeSettings()"/>
    </header>
</template>

<script>
import IconClose from '@img/icons/lucide/x.svg?vue';
import IconLoopa from '@img/icons/lucide/search.svg?vue';
import IconAddressbook from '@img/icons/lucide/book-user.svg?vue';
import IconSettings from '@img/icons/lucide/settings.svg?vue';
import IconBurger from '@img/icons/lucide/menu.svg?vue';
import AppSearch from '~/components/search/AppSearch.vue';
import AppSettings from '~/components/settings/AppSettings.vue';
import { mapState } from 'vuex';
import { IS_TESTNET } from '~/config';
import AppHeaderDropdown from './header/LinksDropdown.vue';
import AppHeaderAddressbook from './header/AppHeaderAddressbook.vue';

// Keep the header in place until the page is scrolled at least that far:
const HEADER_HIDE_OFFSET = 120;

// Minimal scroll distance that flips the header visibility:
const SCROLL_DELTA_THRESHOLD = 6;

// Past this point the header picks up a shadow, so the content reads as passing under it:
const SCROLL_EDGE_OFFSET = 4;

export default {
    data() {
        return {
            searchVisible: false,
            activeUrl: 'index',
            currentRoute: undefined,
            showDropdown: false,
            isHiding: false,
            showAddressbook: false,
            headerHiddenByScroll: false,
            isScrolled: false,
            lastScrollPosition: 0,
            navThumbStyle: { opacity: 0 },
            navThumbReady: false,
        };
    },

    computed: {
        IS_TESTNET() {
            return IS_TESTNET;
        },
        showHeaderMain() {
            return this.$route.name === 'index' && (!this.isSettingsOpened || (this.isSettingsOpened && !this.isMobile));
        },
        links() {
            if (IS_TESTNET) {
                return [
                    'blocks',
                    'validators',
                    'whales',
                ];
            }
            return [
                'stats',
                'blocks',
                'apps',
            ];
        },
        canShowDropdown() {
            return !this.isHiding && this.showDropdown;
        },

        /**
         * The addressbook button is always there on inner pages, and only shows up on the
         * index page once there is something saved in it.
         */
        canShowAddressbook() {
            return this.$route.name !== 'index'
                || (this.appLocalAddressbook && this.appLocalAddressbook.length > 0);
        },

        /**
         * The header may slide away only when it's sticky. On the index page it's
         * a part of the page flow and scrolls away on its own.
         */
        canHideOnScroll() {
            return !this.showHeaderMain;
        },

        /**
         * Never hide the header while something is opened inside of it.
         */
        headerIsHidden() {
            return this.headerHiddenByScroll
                && this.canHideOnScroll
                && !this.searchVisible
                && !this.showAddressbook
                && !this.isSettingsOpened;
        },
        ...mapState(['appLocalAddressbook']),
    },

    watch: {
        $route: ['getActiveLink', 'closeAddressbook', 'showHeader', 'updateNavThumb'],
    },

    created() {
        this.getActiveLink();
    },

    mounted() {
        this.lastScrollPosition = window.scrollY;
        this.isScrolled = window.scrollY > SCROLL_EDGE_OFFSET;
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.updateNavThumb);
        this.updateNavThumb();

        // Link widths settle once the web font is in:
        if (document.fonts?.ready) {
            document.fonts.ready.then(this.updateNavThumb);
        }
    },

    beforeDestroy() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.updateNavThumb);
    },

    methods: {
        /**
         * Moves the underline to the active top-level link. Measured from the live DOM
         * so it follows the font, the locale and the viewport without magic numbers.
         * The line spans the label only, without the anchor paddings.
         * The very first placement is applied without a transition, otherwise the
         * line would visibly fly in from the left corner on page load.
         */
        updateNavThumb() {
            this.$nextTick(() => {
                const nav = this.$refs.linksNav;
                const activeEl = nav && nav.querySelector('.header-link--active');

                if (!activeEl) {
                    this.navThumbStyle = { opacity: 0 };
                    return;
                }

                // The label's offset parent is the link itself:
                const label = activeEl.querySelector('.header-link__label');

                this.navThumbStyle = {
                    opacity: 1,
                    transform: `translateX(${activeEl.offsetLeft + label.offsetLeft}px)`,
                    width: `${label.offsetWidth}px`,
                    transition: this.navThumbReady ? undefined : 'none',
                };

                if (!this.navThumbReady) {
                    requestAnimationFrame(() => {
                        this.navThumbReady = true;
                    });
                }
            });
        },

        getActiveLink() {
            this.currentRoute = this.$router.currentRoute.name;
            this.activeUrl = this.links.filter(link => link === this.currentRoute)[0];
        },

        dropdownEnter() {
            this.showDropdown = true;
        },

        dropdownLeave() {
            this.isHiding = true;
            this.showDropdown = false;
            setTimeout(() => {
                this.isHiding = false;
            }, 400);
        },

        closeAddressbook() {
            this.showAddressbook = false;
        },

        showHeader() {
            this.headerHiddenByScroll = false;
            this.lastScrollPosition = window.scrollY;
        },

        /**
         * Hide the header when scrolling down, bring it back when scrolling up.
         */
        handleScroll() {
            const position = Math.max(window.scrollY, 0);
            const delta = position - this.lastScrollPosition;

            this.isScrolled = position > SCROLL_EDGE_OFFSET;

            // Ignore tiny movements and iOS rubber banding:
            if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) {
                return;
            }

            this.lastScrollPosition = position;
            this.headerHiddenByScroll = delta > 0 && position > HEADER_HIDE_OFFSET;
        },
    },
    inject: ['closeSettings', 'toggleSettings', 'isSettingsOpened'],

    components: {
        IconClose,
        IconLoopa,
        IconSettings,
        AppSearch,
        AppSettings,
        IconAddressbook,
        IconBurger,
        AppHeaderDropdown,
        AppHeaderAddressbook,
    },
};
</script>

<style lang="scss">
// The bar is a translucent material: content scrolls underneath and shows through the blur.
.header {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 80000;
    width: 100%;
    height: 56px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    background: var(--header-surface-background);
    border-bottom: 1px solid var(--header-border-bottom-color);
    backdrop-filter: blur(20px) saturate(160%);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    box-shadow: 0 8px 24px -16px transparent;
    transition: transform .28s cubic-bezier(.2, .8, .2, 1),
                box-shadow .3s ease,
                background-color .2s ease;

    &--scrolled {
        box-shadow: 0 8px 24px -16px var(--header-box-shadow-color);
    }

    &--hidden {
        transform: translateY(-100%);
    }

    &__container {
        flex-grow: 1;
        align-self: stretch;
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
    }

    &__brand {
        display: flex;
        align-items: center;
        align-self: stretch;
        flex-shrink: 0;
        gap: 6px;
    }

    &__logo-link {
        display: flex;
        border-radius: 12px;
        &:focus-visible {
            outline: 2px solid var(--blue-bright);
            outline-offset: 2px;
        }
    }

    &__logo {
        display: block;
        width: 40px;
        height: 40px;
        color: var(--header-logo-color);
        transition: color .15s ease, transform .15s ease;
        &:hover {
            color: var(--blue-bright);
        }
        &:active {
            transform: scale(.94);
        }
    }

    // Full height of the bar, so the underline can sit right on its bottom edge:
    &__links {
        position: relative;
        display: flex;
        align-items: center;
        align-self: stretch;
        gap: 6px;
        margin-left: 6px;
    }

    &__search {
        flex: 1 1 auto;
        display: flex;
        justify-content: flex-end;
        min-width: 0;
        padding: 0 4px 0 12px;
    }

    &__actions {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        gap: 4px;
        margin-left: auto;
    }
}

// Index page: the bar is just a row above the hero, nothing to float over yet.
.header-main {
    position: relative;
    height: 60px;
    background: transparent;
    border-bottom-color: transparent;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
}

// Mobile menu open: solid so the menu below doesn't bleed through.
.header-no-shadow {
    box-shadow: none;
    border-bottom-color: transparent;
    background: var(--header-surface-background-solid);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
}

.header-nav-thumb {
    position: absolute;
    bottom: -1px; // over the border of the bar
    left: 0;
    height: 3px;
    border-radius: 3px 3px 0 0;
    background: var(--blue-bright);
    pointer-events: none;
    z-index: 0;
    transition: transform .32s cubic-bezier(.22, 1, .36, 1),
                width .32s cubic-bezier(.22, 1, .36, 1),
                opacity .2s ease;
}

.header-link {
    position: relative;
    z-index: 1;
    user-select: none;

    &__anchor {
        display: flex;
        align-items: center;
        gap: 7px;
        height: 36px;
        padding: 0 13px;
        border-radius: 10px;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: -0.01em;
        color: var(--body-muted-text-color);
        text-decoration: none !important;
        cursor: pointer;
        transition: color .15s ease, transform .1s ease;

        // Hover only brightens the text; the underline stays with the active link:
        &:hover {
            color: var(--body-text-color);
        }
        &:active {
            transform: scale(.96);
        }
        &:focus-visible {
            outline: 2px solid var(--blue-bright);
            outline-offset: -2px;
        }
    }

    &--active &__anchor {
        color: var(--body-text-color);
    }
}

.header-icon-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--body-muted-text-color);
    cursor: pointer;
    transition: color .15s ease, background-color .15s ease, transform .1s ease;

    svg {
        display: block;
        width: 20px;
        height: 20px;
        stroke: currentColor;
        fill: none;
    }

    &:hover {
        color: var(--body-text-color);
        background-color: var(--icon-button-hover-background);
    }
    &:active {
        transform: scale(.92);
    }
    &:focus-visible {
        outline: 2px solid var(--blue-bright);
        outline-offset: 2px;
    }

    &--loopa {
        display: none;
        z-index: 80010;
    }
    &--menu {
        z-index: 80001;
    }
    &--pressed {
        color: var(--body-text-color);
        background-color: var(--tab-thumb-background);
        box-shadow: var(--tab-thumb-shadow);
    }
}

// Settings hang from the gear like the addressbook hangs from its button:
.settings-dropdown {
    position: absolute;
    top: 46px;
    right: 0;
    z-index: 1002;
    width: 372px;
    box-sizing: border-box;
    background: var(--surface-elevated-background);
    border: 1px solid var(--card-border-color);
    border-radius: 14px;
    box-shadow: 0 1px 2px var(--card-box-shadow-color), 0 16px 40px -12px var(--card-box-shadow-color);
    overflow: hidden;
    text-align: left;
    cursor: default;

}

.header-addressbook-container {
    display: inline-flex;
    position: relative;
}

// The dropdown hangs from the right edge of the button that opened it:
.header .header-addressbook-container {
    .addressbook-dropdown {
        top: 46px;
        right: 0;
    }
}

.header-search {
    width: 100%;
    max-width: 340px;
    font-size: 14px;
    transition: max-width .3s cubic-bezier(.2, .8, .2, 1);

    &:focus-within {
        max-width: 480px;
    }

    .search-input {
        height: 38px;
        align-items: center;
        border-radius: 12px;
        border: 1px solid transparent;
        background: var(--body-light-muted-color);
        color: var(--body-text-color);
        transition: background-color .15s ease,
                    border-color .15s ease,
                    box-shadow .15s ease;

        &__loopa {
            width: 16px;
            height: 16px;
            margin: 0 -2px 0 12px;
            opacity: .55;
        }
        &__input {
            height: 100%;
            padding: 0 12px;
            font-size: 14px;
            &::placeholder {
                color: var(--body-muted-text-color);
            }
            &::-webkit-input-placeholder {
                color: var(--body-muted-text-color);
            }
        }
        &:focus-within {
            outline: 0 none;
            background: var(--card-background);
            border-color: var(--blue-bright) !important;
            box-shadow: 0 0 0 3px var(--blue-bright-opacity);
        }
        &--expanded {
            border-width: 1px !important;
        }
    }

    .search-input-controls {
        padding: 0 8px 0 4px;
        &__hotkey {
            min-width: 20px;
            height: 20px;
            border-radius: 5px;
            font-size: 11px;
        }
        &__loader {
            transform: scale(0.9);
        }
    }

    .search-results {
        padding-bottom: 4px;
        padding-top: 4px;
        overflow: hidden;
        &__link {
            margin: 0 0 0 0;
        }
    }
    .search-result {
        &__left {
            margin-left: -4px;
        }
    }
}

.fade-enter, .fade-leave-to {
    opacity: 0;
    transform: translateY(6px) scale(.98);
}

.fade-enter-active, .fade-leave-active {
    transition: opacity .18s ease, transform .22s cubic-bezier(.2, .8, .2, 1);
}

@media (prefers-reduced-motion: reduce) {
    .header,
    .header-nav-thumb,
    .header-search,
    .fade-enter-active, .fade-leave-active {
        transition: none;
    }
}

@media (prefers-reduced-transparency: reduce) {
    .header {
        background: var(--header-surface-background-solid);
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
    }
}

@media screen and (max-width: 1250px) {
    .header__container {
        padding: 0 12px;
    }
}

@media screen and (max-width: 880px) {
    .header__links {
        display: none;
    }
}

// Touch screens keep no hover state around after a tap:
@media screen and (max-width: 600px) {
    .header-icon-button:hover {
        color: var(--body-muted-text-color);
        background: transparent;
    }
    .header-icon-button:active {
        color: var(--body-text-color);
    }
}

@media screen and (min-width: 600px) and (max-width: 665px) {
    // Making some space for ton connect button
    .header-search {
        max-width: 280px;
    }
}

@media screen and (max-width: 480px) {
    .header {
        margin-bottom: 12px;
    }

    // Small screen: the search field is hidden UNLESS the loopa button was tapped,
    // and then it takes the whole bar:
    .header__search {
        display: none;
        padding: 0;

        .header--mobile-search-visible & {
            display: flex;
        }
    }

    .header-search {
        max-width: none;
        &:focus-within {
            max-width: none;
        }
    }

    .header--mobile-search-visible {
        .header__brand,
        .header__actions {
            display: none;
        }
    }

    .header-icon-button--loopa {
        display: inline-flex;
    }
}

.new-badge {
    font-size: 9px;
    font-weight: 500;
    border-radius: 4px;
    padding: 3px 4px;
    display: inline;
    background-color: var(--filter-item-active);
    color: #FFFFFF;
    margin-left: 5px;

    &:hover {
        color: white;
    }
}

.addressbook-overlay {
    position: fixed;
    width: 100%;
    top: 0;
    left: 0;
    height: 100dvh;
    z-index: 1001;
}
</style>
